'use client';

import { Box, VStack, HStack } from '@terra/ui-styled-system';
import { Text } from '../../../../../components/styled/text';
import { Badge } from '../../../../../components/badge';
import {
  Tick01Icon,
  ArrowDown01Icon,
  ArrowRight01Icon,
  Copy01Icon,
  RadioIcon,
} from '@terra/ui-icons';
import { useState, useMemo, useCallback } from 'react';
import { IconButton } from '../../../../../components/icon-button';
import { useTranslations } from 'next-intl';
import { z } from 'zod';

type SseClientRendererProps = {
  data: unknown;
};
const SseMessageSchema = z.object({
  timestamp: z.string().optional(),
  data: z.string().optional(),
  type: z.string().optional(),
  id: z.union([z.string(), z.number()]).optional(),
});
const SseClientDataSchema = z.object({
  mode: z.enum(['connected', 'poll', 'stopped', 'unknown']).optional(),
  sessionId: z.string().optional(),
  url: z.string().optional(),
  status: z.string().optional(),
  count: z.number().optional(),
  messages: z.array(SseMessageSchema).optional(),
  isActive: z.boolean().optional(),
  rawResult: z.string().optional(),
});

type ParsedSseMessage = z.infer<typeof SseMessageSchema>;
type ParsedSseData = {
  mode: 'connected' | 'poll' | 'stopped' | 'unknown';
  sessionId?: string;
  url?: string;
  status?: string;
  count?: number;
  messages?: ParsedSseMessage[];
  isActive?: boolean;
  rawResult?: string;
};

function parseSseData(data: unknown): ParsedSseData {
  const result = SseClientDataSchema.safeParse(data);

  if (!result.success) {
    return { mode: 'unknown' };
  }

  const obj = result.data;
  let mode: ParsedSseData['mode'] = obj.mode ?? 'unknown';

  if (mode === 'unknown') {
    if (obj.status === 'active' && obj.url) {
      mode = 'connected';
    } else if (obj.messages !== undefined) {
      mode = obj.isActive === false ? 'stopped' : 'poll';
    }
  }

  return {
    mode,
    sessionId: obj.sessionId,
    url: obj.url,
    status: obj.status,
    count: obj.count ?? obj.messages?.length,
    messages: obj.messages,
    isActive: obj.isActive,
    rawResult: obj.rawResult,
  };
}

// ============================================================================
// Connected view
// ============================================================================
function ConnectedView({
  url,
  sessionId,
}: {
  url: string;
  sessionId?: string;
}) {
  const t = useTranslations('designSystem.chat.sseClient');
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(() => {
    if (!sessionId) return;
    navigator.clipboard.writeText(sessionId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [sessionId]);

  return (
    <VStack gap={2} width="100%" alignItems="stretch">
      <HStack gap={1.5}>
        <Badge size="sm" variant="fill">
          {t('connected')}
        </Badge>
      </HStack>
      <Box
        bg="bg.canvas"
        p={2}
        borderRadius="sm"
        borderWidth="1px"
        borderColor="border.subtle"
        width="100%"
      >
        <VStack gap={1.5} alignItems="stretch">
          <HStack gap={1}>
            <Text fontSize="xs" color="fg.muted" flexShrink={0}>
              {t('endpoint')}
            </Text>
            <Text
              fontSize="xs"
              fontFamily="mono"
              color="fg.default"
              wordBreak="break-all"
            >
              {url}
            </Text>
          </HStack>
          {sessionId && (
            <HStack gap={1} justifyContent="space-between">
              <HStack gap={1} flex="1" minW={0}>
                <Text fontSize="xs" color="fg.muted" flexShrink={0}>
                  {t('sessionId')}
                </Text>
                <Text
                  fontSize="xs"
                  fontFamily="mono"
                  color="fg.subtle"
                  wordBreak="break-all"
                >
                  {sessionId}
                </Text>
              </HStack>
              <IconButton
                variant="ghost"
                size="xs"
                aria-label={t('copySessionId')}
                onClick={handleCopy}
                flexShrink={0}
              >
                {copied ? <Tick01Icon size={12} /> : <Copy01Icon size={12} />}
              </IconButton>
            </HStack>
          )}
        </VStack>
      </Box>
      <Text fontSize="xs" color="fg.muted">
        {t('connectedHint')}
      </Text>
    </VStack>
  );
}

// ============================================================================
// Event row (expandable)
// ============================================================================
function EventRow({
  event,
  index,
  isExpanded,
  onToggle,
}: {
  event: ParsedSseMessage;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const t = useTranslations('designSystem.chat.sseClient');
  const formattedTimestamp = useMemo(() => {
    if (!event.timestamp) return null;

    try {
      return new Date(event.timestamp).toLocaleTimeString();
    } catch {
      return event.timestamp;
    }
  }, [event.timestamp]);
  const previewData = event.data
    ? event.data.length > 80
      ? `${event.data.slice(0, 80)}…`
      : event.data
    : null;

  return (
    <Box
      borderWidth="1px"
      borderColor="border.subtle"
      borderRadius="md"
      width="100%"
    >
      <HStack
        p={1.5}
        justify="space-between"
        cursor="pointer"
        onClick={onToggle}
        _hover={{ bg: 'bg.subtle' }}
        width="100%"
      >
        <HStack gap={1.5} flex="1" minW={0}>
          <IconButton size="xs" variant="ghost" aria-label={t('toggleEvent')}>
            {isExpanded ? (
              <ArrowDown01Icon size={12} />
            ) : (
              <ArrowRight01Icon size={12} />
            )}
          </IconButton>
          <Text fontSize="xs" color="fg.muted" fontFamily="mono" flexShrink={0}>
            #{index + 1}
          </Text>
          {event.type && event.type !== 'message' && (
            <Badge size="sm" variant="emphasis">
              {event.type}
            </Badge>
          )}
          {previewData && (
            <Text
              fontSize="xs"
              fontFamily="mono"
              color="fg.default"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              flex="1"
            >
              {previewData}
            </Text>
          )}
        </HStack>
        {formattedTimestamp && (
          <Text
            fontSize="xs"
            color="fg.subtle"
            fontFamily="mono"
            flexShrink={0}
          >
            {formattedTimestamp}
          </Text>
        )}
      </HStack>

      {isExpanded && (
        <Box
          borderTopWidth="1px"
          borderColor="border.subtle"
          p={2}
          bg="bg.canvas"
        >
          <VStack alignItems="start" gap={1.5} width="100%">
            {event.id !== undefined && (
              <HStack gap={1}>
                <Text fontSize="xs" color="fg.muted">
                  {t('eventId')}
                </Text>
                <Text fontSize="xs" fontFamily="mono" color="fg.default">
                  {String(event.id)}
                </Text>
              </HStack>
            )}
            {event.data && (
              <VStack alignItems="start" gap={1} width="100%">
                <Text fontSize="xs" color="fg.muted" fontWeight="medium">
                  {t('data')}
                </Text>
                <Box p={2} bg="bg.muted" borderRadius="sm" width="100%">
                  <Text
                    fontSize="xs"
                    fontFamily="mono"
                    color="fg.subtle"
                    whiteSpace="pre-wrap"
                    wordBreak="break-all"
                  >
                    {event.data}
                  </Text>
                </Box>
              </VStack>
            )}
          </VStack>
        </Box>
      )}
    </Box>
  );
}

// ============================================================================
// Events list (poll / stopped)
// ============================================================================
function EventsView({
  messages,
  count,
  isActive,
  sessionId,
}: {
  messages?: ParsedSseMessage[];
  count?: number;
  isActive?: boolean;
  sessionId?: string;
}) {
  const t = useTranslations('designSystem.chat.sseClient');
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const hasEvents = messages && messages.length > 0;

  return (
    <VStack gap={2} width="100%" alignItems="stretch">
      <HStack gap={1.5} justifyContent="space-between">
        <HStack gap={1.5}>
          <RadioIcon size={12} color="var(--colors-fg-muted)" />
          <Text fontSize="xs" color="fg.muted">
            {hasEvents
              ? t('eventsCollected', { count: count ?? messages?.length ?? 0 })
              : t('noEvents')}
          </Text>
        </HStack>
        {isActive !== undefined && (
          <Badge size="sm" variant="fill">
            {isActive ? t('active') : t('stopped')}
          </Badge>
        )}
      </HStack>

      {sessionId && (
        <HStack gap={1}>
          <Text fontSize="xs" color="fg.muted" flexShrink={0}>
            {t('sessionId')}
          </Text>
          <Text
            fontSize="xs"
            fontFamily="mono"
            color="fg.subtle"
            wordBreak="break-all"
          >
            {sessionId}
          </Text>
        </HStack>
      )}

      {hasEvents ? (
        <VStack gap={1.5} width="100%" alignItems="stretch" pr={1}>
          {messages.map((event, index) => (
            <EventRow
              key={index}
              event={event}
              index={index}
              isExpanded={expandedIndex === index}
              onToggle={() =>
                setExpandedIndex(expandedIndex === index ? null : index)
              }
            />
          ))}
        </VStack>
      ) : (
        <Box bg="bg.subtle" p={3} borderRadius="sm" width="100%">
          <Text fontSize="xs" color="fg.muted" textAlign="center">
            {t('noEventsHint')}
          </Text>
        </Box>
      )}
    </VStack>
  );
}

// ============================================================================
// Main component
// ============================================================================
export function SseClientRenderer({ data }: SseClientRendererProps) {
  const parsed = useMemo(() => parseSseData(data), [data]);

  if (parsed.mode === 'connected' && parsed.url) {
    return <ConnectedView url={parsed.url} sessionId={parsed.sessionId} />;
  }

  if (parsed.mode === 'poll' || parsed.mode === 'stopped') {
    return (
      <EventsView
        messages={parsed.messages}
        count={parsed.count}
        isActive={parsed.isActive}
        sessionId={parsed.sessionId}
      />
    );
  }

  if (parsed.rawResult) {
    return (
      <Box
        bg="bg.canvas"
        p={2}
        borderRadius="sm"
        borderWidth="1px"
        borderColor="border.subtle"
        width="100%"
      >
        <Text
          fontSize="xs"
          fontFamily="mono"
          color="fg.muted"
          whiteSpace="pre-wrap"
          wordBreak="break-word"
        >
          {parsed.rawResult}
        </Text>
      </Box>
    );
  }

  if (typeof data === 'string' && data.trim()) {
    return (
      <Box
        bg="bg.canvas"
        p={2}
        borderRadius="sm"
        borderWidth="1px"
        borderColor="border.subtle"
        width="100%"
      >
        <Text
          fontSize="xs"
          fontFamily="mono"
          color="fg.muted"
          whiteSpace="pre-wrap"
          wordBreak="break-word"
        >
          {data}
        </Text>
      </Box>
    );
  }

  return null;
}
